/**
 * The guest cart and order submission.
 *
 * IDEMPOTENCY: `POST /customer/orders` REQUIRES an `Idempotency-Key` (8–128
 * chars). The key is generated once per submission attempt and REUSED on retry,
 * so a phone that loses signal mid-request and retries gets the stored response
 * back (`Idempotent-Replay: true`) instead of a second order. Generating a new
 * key per tap is exactly how a table ends up paying for two dinners.
 */

import { useRef, useState } from 'react';
import { EmptyState } from '@sonauto/ui';
import { useCustomerI18n } from './CustomerI18nProvider';
import { guestCartTotal, guestLineTotal, removeGuestLine, setGuestQuantity, toGuestOrderItems } from './cart';
import type { GuestCart } from './cart';
import { newIdempotencyKey } from '../lib/api';
import type { CustomerApi } from '../lib/api';
import { errorMessage } from '../lib/hooks';

export interface CartScreenProps {
  readonly api: CustomerApi;
  readonly cart: GuestCart;
  readonly setCart: (cart: GuestCart) => void;
  readonly requiresStaffConfirmation: boolean;
  readonly onSubmitted: () => void;
}

export function CartScreen({
  api,
  cart,
  setCart,
  requiresStaffConfirmation,
  onSubmitted,
}: CartScreenProps): React.JSX.Element {
  const { tOr, price } = useCustomerI18n();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Held across retries of THE SAME submission. Cleared only after success.
  const idempotencyKey = useRef<string | null>(null);

  const submit = async (): Promise<void> => {
    if (cart.length === 0) return;
    idempotencyKey.current ??= newIdempotencyKey();
    setBusy(true);
    setError(null);
    try {
      await api.submitOrder(
        { items: toGuestOrderItems(cart), note: note.trim() === '' ? null : note.trim() },
        idempotencyKey.current,
      );
      idempotencyKey.current = null;
      setCart([]);
      setNote('');
      onSubmitted();
    } catch (cause: unknown) {
      // The key is deliberately NOT reset here: the next tap is a retry of the
      // same order, and the server must recognise it as such.
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (cart.length === 0) {
    return (
      <EmptyState
        title={tOr('customer.cart.empty', 'Giỏ hàng đang trống')}
        description={tOr('customer.cart.empty_hint', 'Chọn món trong thực đơn để bắt đầu.')}
      />
    );
  }

  return (
    <div className="px-3 py-4">
      <h2 className="text-flow text-lg font-bold">{tOr('customer.cart.title', 'Giỏ hàng')}</h2>

      <ul className="mt-3 space-y-2">
        {cart.map((line) => (
          <li key={line.key} className="rounded-lg bg-surface p-3 shadow-card ring-1 ring-line">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-flow text-base font-semibold">
                  {line.productName}
                  {line.variantName === null ? '' : ` · ${line.variantName}`}
                </p>
                {line.modifiers.length > 0 ? (
                  <p className="text-flow text-sm text-fg-muted">{line.modifiers.map((m) => m.name).join(', ')}</p>
                ) : null}
                {line.spiceLevel !== null ? <p className="text-sm text-danger-fg">🌶 {line.spiceLevel}/5</p> : null}
                {line.note !== null ? <p className="text-flow text-sm text-fg-subtle">“{line.note}”</p> : null}
              </div>
              <span className="shrink-0 font-bold">{price(guestLineTotal(line))}</span>
            </div>

            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCart(setGuestQuantity(cart, line.key, line.quantity - 1))}
                aria-label={tOr('customer.quantity.decrease', 'Giảm')}
                className="h-11 w-11 rounded-full bg-surface-alt text-xl font-bold"
              >
                −
              </button>
              <span className="w-8 text-center font-bold tabular-nums">{line.quantity}</span>
              <button
                type="button"
                onClick={() => setCart(setGuestQuantity(cart, line.key, line.quantity + 1))}
                aria-label={tOr('customer.quantity.increase', 'Tăng')}
                className="h-11 w-11 rounded-full bg-surface-alt text-xl font-bold"
              >
                +
              </button>
              <button
                type="button"
                onClick={() => setCart(removeGuestLine(cart, line.key))}
                className="text-flow ml-auto min-h-[44px] px-3 text-sm font-medium text-danger-fg"
              >
                {tOr('customer.cart.remove_item', 'Xoá')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <label className="text-flow mt-4 block text-sm font-bold" htmlFor="order-note">
        {tOr('customer.note.label', 'Ghi chú cho cả đơn')}
      </label>
      <textarea
        id="order-note"
        rows={3}
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={tOr('customer.note.placeholder', 'Ví dụ: mang ra cùng lúc, thêm bát nhỏ…')}
        className="mt-1 w-full rounded-md bg-surface-alt p-3 text-base ring-1 ring-line"
      />

      <div className="mt-4 flex items-baseline justify-between rounded-lg bg-surface p-3 ring-1 ring-line">
        <span className="text-flow font-semibold">{tOr('customer.total.subtotal', 'Tạm tính')}</span>
        <span className="text-xl font-bold">{price(guestCartTotal(cart))}</span>
      </div>
      <p className="text-flow mt-1 text-xs text-fg-subtle">
        {tOr('customer.total.tax', 'Thuế và phí phục vụ (nếu có) sẽ được tính trên hoá đơn cuối.')}
      </p>

      {requiresStaffConfirmation ? (
        <p className="text-flow mt-3 rounded-lg bg-info-bg p-3 text-sm text-info-fg">
          {tOr('customer.order.confirm_body', 'Đơn của bạn sẽ được nhân viên xác nhận trước khi vào bếp.')}
        </p>
      ) : null}

      {error !== null ? (
        <p className="text-flow mt-3 rounded-lg bg-danger-bg p-3 text-sm font-semibold text-danger-fg">{error}</p>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={() => void submit()}
        className="text-flow mt-4 min-h-[60px] w-full rounded-lg bg-brand-600 px-4 py-3 text-lg font-bold text-white disabled:opacity-50"
      >
        {busy ? tOr('customer.order.submitting', 'Đang gửi…') : tOr('customer.order.submit', 'Gửi đơn')}
      </button>
    </div>
  );
}
